import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RESIDENT_RELATION_LABELS,
  type Page,
  type Resident,
  type ResidentRelation,
  type ResidentUnitLink,
  type Unit,
} from '@azko/shared';
import { ApiRequestError, api } from '../lib/api.js';
import { useBlocks } from '../lib/useBlocks.js';
import { Alert, Button, Field, Select, cx } from './ui.js';
import { IconClose } from './icons.js';

/**
 * Onde o morador mora — depois de cadastrado.
 *
 * A API de substituir vinculos existe desde o cadastro (`PUT /residents/:id/
 * units`), mas nao havia tela: morador que trocou de apartamento, ou que entrou
 * pela importacao com a unidade errada, so se corrigia no banco. Como o
 * vinculo e o que decide QUEM recebe a encomenda, um erro aqui manda o aviso
 * para a pessoa errada — e nao havia como consertar pelo produto.
 *
 * A tela mostra a lista inteira porque o PUT substitui tudo: editar mostrando
 * so um vinculo apagaria em silencio o segundo apartamento de quem tem dois —
 * que e comum o bastante para a tabela ser N:N.
 */

interface Draft {
  unitId: string;
  unitLabel: string;
  block: string;
  relation: ResidentRelation;
  isPrimary: boolean;
}

function toDraft(link: ResidentUnitLink): Draft {
  return {
    unitId: link.unitId,
    unitLabel: link.unitLabel,
    block: link.block,
    relation: link.relation,
    isPrimary: link.isPrimary,
  };
}

export function ResidentUnitsEditor({
  resident,
  onDone,
}: {
  resident: Resident;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();

  const [links, setLinks] = useState<Draft[]>(resident.units.map(toDraft));
  const [blockId, setBlockId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [relation, setRelation] = useState<ResidentRelation>('owner');
  const [error, setError] = useState<string | null>(null);

  const { data: units } = useQuery({
    queryKey: ['units', 'picker'],
    queryFn: () => api.get<Page<Unit>>('/units?limit=500'),
  });

  const unitItems = units?.items ?? [];
  const blocks = useBlocks(unitItems);

  const available = unitItems.filter(
    (unit) =>
      (!blockId || unit.blockId === blockId) &&
      // Ja vinculado nao volta para a lista: o backend recusa a unidade
      // repetida no mesmo envio, e o erro chegaria so depois de salvar.
      !links.some((link) => link.unitId === unit.id),
  );

  const save = useMutation({
    mutationFn: () =>
      api.put(`/residents/${resident.id}/units`, {
        units: links.map((link) => ({
          unitId: link.unitId,
          relation: link.relation,
          isPrimary: link.isPrimary,
        })),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['residents'] });
      onDone();
    },
    onError: (cause) =>
      setError(cause instanceof ApiRequestError ? cause.message : 'Nao foi possivel salvar.'),
  });

  function add() {
    const unit = unitItems.find((item) => item.id === unitId);
    if (!unit) return;

    setLinks((current) => [
      ...current,
      {
        unitId: unit.id,
        unitLabel: unit.label,
        block: unit.block,
        relation,
        // Primeiro vinculo do morador ja nasce como destinatario padrao; do
        // segundo em diante, a escolha e explicita.
        isPrimary: current.length === 0,
      },
    ]);
    setUnitId('');
    setError(null);
  }

  /** Destinatario padrao e por UNIDADE, entao dois vinculos podem te-lo. */
  function togglePrimary(index: number) {
    setLinks((current) =>
      current.map((link, position) =>
        position === index ? { ...link, isPrimary: !link.isPrimary } : link,
      ),
    );
  }

  return (
    <div className="rounded-2xl border border-line bg-surface-2 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-bold">Unidades de {resident.name}</p>
        <button
          type="button"
          onClick={onDone}
          aria-label="Fechar edicao"
          className="rounded-lg p-1.5 text-faint transition hover:bg-surface hover:text-fg"
        >
          <IconClose className="size-4" />
        </button>
      </div>

      {links.length === 0 ? (
        <p className="mt-3 text-sm text-warn">
          Sem unidade, este morador nao recebe encomenda nenhuma.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {links.map((link, index) => (
            <li
              key={link.unitId}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-line bg-surface px-3 py-2"
            >
              <span className="font-semibold">
                {link.block ? `${link.block} • ${link.unitLabel}` : link.unitLabel}
              </span>
              <span className="text-sm text-muted">{RESIDENT_RELATION_LABELS[link.relation]}</span>

              <label className="ml-auto flex items-center gap-2 text-xs text-muted">
                <input
                  type="checkbox"
                  checked={link.isPrimary}
                  onChange={() => togglePrimary(index)}
                  className="size-4 accent-accent"
                />
                Destinatario padrao
              </label>

              <button
                type="button"
                onClick={() => setLinks((current) => current.filter((_, i) => i !== index))}
                aria-label={`Remover ${link.unitLabel}`}
                className="rounded-lg p-1.5 text-faint transition hover:bg-surface-2 hover:text-danger"
              >
                <IconClose className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Adicionar mais um apartamento — dois imoveis no mesmo predio e o caso
          que fez a tabela nascer N:N. */}
      {links.length < 10 && (
        <div
          className={cx(
            'mt-4 grid gap-3 border-t border-line pt-4',
            blocks.length > 0 ? 'sm:grid-cols-4' : 'sm:grid-cols-3',
          )}
        >
          {blocks.length > 0 && (
            <Field label="Bloco">
              <Select
                value={blockId}
                onChange={(next) => {
                  setBlockId(next);
                  setUnitId('');
                }}
                emptyOption="Todos os blocos"
                options={blocks.map(([id, name]) => ({ value: id, label: name }))}
              />
            </Field>
          )}

          <Field label="Unidade">
            <Select
              value={unitId}
              onChange={setUnitId}
              placeholder="Selecione..."
              options={available.map((unit) => ({
                value: unit.id,
                label: !blockId && unit.block ? `${unit.block} • ${unit.label}` : unit.label,
              }))}
            />
          </Field>

          <Field label="Vinculo">
            <Select
              value={relation}
              onChange={(next) => setRelation(next as ResidentRelation)}
              options={Object.entries(RESIDENT_RELATION_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
            />
          </Field>

          <div className="flex items-end">
            <Button variant="ghost" disabled={!unitId} onClick={add} className="w-full">
              Adicionar
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4">
          <Alert>{error}</Alert>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-3">
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          Salvar
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
